import {
  isArray,
  isBoolean,
  isNonNullish,
  isPlainObject,
  isString,
  mapToObj,
  uniqueBy,
} from 'remeda';
import { parseJsonSafely } from 'src/utils/objects';
import {
  ReconciledSidePanelSection,
  SidePanelLayout,
  SidePanelLayoutEntry,
  SidePanelPropertyKey,
  SidePanelSectionAvailability,
  SidePanelSectionKey,
} from './layout.types';

const SECTION_KEYS: SidePanelSectionKey[] = ['conversation', 'site', 'client'];

export function jsonParseIfString(raw: unknown): unknown {
  if (!isString(raw)) {
    return raw;
  }

  return parseJsonSafely(raw);
}

export function parseLayout(raw: unknown): SidePanelLayout | null {
  const candidate = jsonParseIfString(raw);

  if (!isPlainObject(candidate) || !isPlainObject(candidate.sections)) {
    return null;
  }

  const sections: SidePanelLayout['sections'] = {};

  for (const sectionKey of SECTION_KEYS) {
    const section = candidate.sections[sectionKey];

    if (isPlainObject(section) && isArray(section.properties)) {
      sections[sectionKey] = {
        properties: section.properties
          .map((entry) =>
            isPlainObject(entry) && isPropertyKey(entry.key) && isBoolean(entry.visible)
              ? { key: entry.key, visible: entry.visible }
              : null,
          )
          .filter(isNonNullish),
      };
    }
  }

  return { hideEmpty: candidate.hideEmpty === true, sections };
}

export function buildLayout(
  sections: ReconciledSidePanelSection[],
  hideEmpty: boolean,
): SidePanelLayout {
  return {
    hideEmpty,
    sections: mapToObj(sections, ({ key, properties }) => [key, { properties }]),
  };
}

export function reconcileLayout(
  layout: SidePanelLayout,
  sections: SidePanelSectionAvailability[],
): ReconciledSidePanelSection[] {
  return sections.map(({ key, availableKeys, defaultVisibleKeys, groupKeyByProperty }) => {
    const available = new Set(availableKeys);

    const defaultVisible = new Set(defaultVisibleKeys);

    const stored = uniqueBy(
      (layout.sections[key]?.properties ?? []).filter((entry) => available.has(entry.key)),
      (entry) => entry.key,
    );

    const storedKeys = new Set(stored.map((entry) => entry.key));

    const added = availableKeys
      .filter((availableKey) => !storedKeys.has(availableKey))
      .map((availableKey) => ({
        key: availableKey,
        visible: defaultVisible.has(availableKey),
      }));

    return { key, properties: groupsLast([...stored, ...added], groupKeyByProperty) };
  });
}

function groupsLast(
  properties: SidePanelLayoutEntry[],
  groupKeyByProperty: SidePanelSectionAvailability['groupKeyByProperty'],
): SidePanelLayoutEntry[] {
  if (!groupKeyByProperty) {
    return properties;
  }

  const emitted = new Set<string>();

  const groups = properties.flatMap((entry) => {
    const group = groupKeyByProperty[entry.key];

    if (!group || emitted.has(group)) {
      return [];
    }

    emitted.add(group);
    return properties.filter((sibling) => groupKeyByProperty[sibling.key] === group);
  });

  return [...properties.filter((entry) => !groupKeyByProperty[entry.key]), ...groups];
}

function isPropertyKey(raw: unknown): raw is SidePanelPropertyKey {
  return isString(raw) && (raw.startsWith('base:') || raw.startsWith('ca:'));
}
